import { useState, useEffect, useMemo, useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiFileText, FiBarChart2, FiUsers, FiDollarSign, FiSettings, FiUser, FiLogOut } from 'react-icons/fi';
import { QrCode, Smartphone } from 'lucide-react';
import { cpApi } from '../api/api';
import { formatCurrency } from '../utils/formatNumber';
import { intlLocaleFor } from '../i18n/config';
import type { Counterparty, CounterpartyTeam, VNDTierRateRow } from '../types';

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  user: Counterparty | null;
  team: CounterpartyTeam | null;
  teamBalance: number;
  myBalance?: number;
  isResponsible?: boolean;
}

export default function CounterpartySidebar({ isOpen, onToggle, user, team, teamBalance, myBalance = 0, isResponsible = false }: Props) {
  const { t, i18n } = useTranslation();
  const locale = intlLocaleFor(i18n.language);
  const location = useLocation();
  const navigate = useNavigate();

  const MENU_BASE = useMemo(
    () => [
      { name: t('nav.qrProfiles'), path: '/qr-profiles', icon: QrCode },
      { name: t('nav.smsPhones'), path: '/sms-phones', icon: Smartphone },
      { name: t('nav.requests'), path: '/requests', icon: FiFileText },
      { name: t('nav.payouts'), path: '/payouts', icon: FiDollarSign },
      { name: t('nav.dashboard'), path: '/dashboard', icon: FiBarChart2, responsibleOnly: true },
      { name: t('nav.team'), path: '/team', icon: FiUsers },
      { name: t('nav.transactions'), path: '/transactions', icon: FiDollarSign },
      { name: t('nav.settings'), path: '/settings', icon: FiSettings },
    ],
    [t]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPagination, setShowPagination] = useState(false);
  const [vndTiers, setVndTiers] = useState<VNDTierRateRow[]>([]);
  const [vndTiersLoading, setVndTiersLoading] = useState(true);
  const [vndTiersError, setVndTiersError] = useState<string | null>(null);
  const [vndPayoutTiers, setVndPayoutTiers] = useState<Array<{ id?: number; sort_order?: number; min_vnd: number; max_vnd: number; rate_percent: number }>>([]);
  const [vndPayoutTiersLoading, setVndPayoutTiersLoading] = useState(true);
  const [vndPayoutTiersError, setVndPayoutTiersError] = useState<string | null>(null);
  const [inWorkRequestsCount, setInWorkRequestsCount] = useState<number | null>(null);
  const [payoutAvailableCount, setPayoutAvailableCount] = useState<number | null>(null);
  const [payoutActiveCount, setPayoutActiveCount] = useState<number | null>(null);

  const refreshInWorkRequestsCount = useCallback(async () => {
    try {
      const n = await cpApi.getVndSidebarActiveRequestsTotal();
      setInWorkRequestsCount(Number.isFinite(n) ? n : 0);
    } catch {
      setInWorkRequestsCount((prev) => (prev === null ? 0 : prev));
    }
  }, []);

  const refreshPayoutCounts = useCallback(async () => {
    if (!user?.can_process_payouts) {
      setPayoutAvailableCount(0);
      setPayoutActiveCount(0);
      return;
    }
    try {
      const counts = await cpApi.getVndSidebarPayoutCounts();
      setPayoutAvailableCount(Number.isFinite(counts.available) ? counts.available : 0);
      setPayoutActiveCount(Number.isFinite(counts.active) ? counts.active : 0);
    } catch {
      setPayoutAvailableCount((prev) => (prev === null ? 0 : prev));
      setPayoutActiveCount((prev) => (prev === null ? 0 : prev));
    }
  }, [user?.can_process_payouts]);

  useEffect(() => {
    void refreshInWorkRequestsCount();
    void refreshPayoutCounts();
    const onRefresh = () => void refreshInWorkRequestsCount();
    const onPayoutRefresh = () => void refreshPayoutCounts();
    window.addEventListener('vndInWorkCountRefresh', onRefresh);
    window.addEventListener('refreshRequests', onRefresh);
    window.addEventListener('refreshRequests', onPayoutRefresh);
    const intervalId = window.setInterval(onRefresh, 60_000);
    const payoutIntervalId = window.setInterval(onPayoutRefresh, 60_000);
    return () => {
      window.removeEventListener('vndInWorkCountRefresh', onRefresh);
      window.removeEventListener('refreshRequests', onRefresh);
      window.removeEventListener('refreshRequests', onPayoutRefresh);
      window.clearInterval(intervalId);
      window.clearInterval(payoutIntervalId);
    };
  }, [refreshInWorkRequestsCount, refreshPayoutCounts]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setVndTiersLoading(true);
      setVndTiersError(null);
      setVndPayoutTiersLoading(true);
      setVndPayoutTiersError(null);
      try {
        const [{ tiers }, payout] = await Promise.all([
          cpApi.getVndTeamTiers(),
          user?.can_process_payouts ? cpApi.getVndTeamPayoutTiers() : Promise.resolve({ team_id: 0, tiers: [] }),
        ]);
        if (!cancelled) {
          setVndTiers(Array.isArray(tiers) ? tiers : []);
          setVndPayoutTiers(Array.isArray(payout?.tiers) ? payout.tiers : []);
        }
      } catch {
        if (!cancelled) {
          setVndTiers([]);
          setVndTiersError(t('sidebar.rateLoadError'));
          setVndPayoutTiers([]);
          setVndPayoutTiersError(t('sidebar.rateLoadError'));
        }
      } finally {
        if (!cancelled) {
          setVndTiersLoading(false);
          setVndPayoutTiersLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, user?.can_process_payouts]);

  const formatRangeInt = (num: number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);

  const sortedVndTiers = [...vndTiers].sort(
    (a, b) => (a.sort_order ?? a.min_vnd) - (b.sort_order ?? b.min_vnd)
  );
  const sortedVndPayoutTiers = [...vndPayoutTiers].sort(
    (a, b) => (a.sort_order ?? a.min_vnd) - (b.sort_order ?? b.min_vnd)
  );

  const menu = MENU_BASE.filter((item) => {
    if ((item as { responsibleOnly?: boolean }).responsibleOnly) return isResponsible;
    if (item.path === '/team') return isResponsible;
    if (item.path === '/transactions') return isResponsible;
    if (item.path === '/payouts') return !!user?.can_process_payouts;
    return true;
  });
  useEffect(() => {
    setShowPagination(false);
    setCurrentPage(1);
    setTotalPages(1);
  }, [location.pathname]);

  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ currentPage: number; totalPages: number; showPagination: boolean }>).detail;
      if (d) {
        setCurrentPage(d.currentPage);
        setTotalPages(d.totalPages);
        setShowPagination(d.showPagination);
      }
    };
    window.addEventListener('paginationUpdate', h);
    return () => window.removeEventListener('paginationUpdate', h);
  }, []);

  const handlePageChange = (page: number) => {
    window.dispatchEvent(new CustomEvent('pageChange', { detail: { page } }));
  };

  const handleLogout = () => {
    localStorage.removeItem('airex_cp_token');
    window.location.href = '/login';
  };

  const handleCopyBalance = async (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const balanceNumber = (isResponsible ? teamBalance : myBalance).toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    try {
      await navigator.clipboard.writeText(balanceNumber);
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: t('sidebar.balanceCopied'), type: 'success' } }));
    } catch {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: t('sidebar.copyError'), type: 'error' } }));
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onToggle} />
      )}
      <aside
        className={`vnd-chrome-sidebar fixed top-0 left-0 z-50 h-dvh overflow-hidden text-white transition-transform lg:static lg:z-10 lg:translate-x-0 w-[250px] max-w-[88vw] flex flex-col border-r border-red-400/20 dark:border-r-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="relative z-10 flex flex-col flex-1 min-h-0">
        <div className="px-4 pt-4 pb-[14px] border-b border-red-400/25 dark:border-white/10 shrink-0">
          <h1 className="text-lg font-bold">{t('sidebar.title')}</h1>
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            <svg className="w-4 h-4 shrink-0 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <p className="text-sm font-medium text-white/90 truncate">{isResponsible && team ? team.name : (user?.name || t('sidebar.counterparty'))}</p>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <nav className="p-4 relative">
          <div className="space-y-2 relative">
            {menu.map(({ name, path, icon: Icon }) => {
              const active = location.pathname === path;
              const showWorkBadge = path === '/requests';
              const showPayoutBadges = path === '/payouts';
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={() => onToggle()}
                  title={
                    showWorkBadge
                      ? t('sidebar.inWorkRequestsTitle')
                      : showPayoutBadges
                        ? `${t('sidebar.payoutAvailableTitle')} / ${t('sidebar.payoutActiveTitle')}`
                        : undefined
                  }
                  className={`relative flex items-center gap-3 px-4 h-12 min-h-12 rounded-xl transition-all duration-200 z-10 ${
                    active ? 'text-white bg-white/20 shadow-lg' : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="truncate min-w-0 flex-1 text-left">{name}</span>
                  {showWorkBadge && (
                    <span
                      className={`shrink-0 min-w-[1.5rem] text-center text-xs font-bold tabular-nums rounded-md px-1.5 py-0.5 ${
                        (inWorkRequestsCount ?? 0) > 0 ? 'bg-amber-400/90 text-amber-950' : 'bg-white/15 text-white/80'
                      }`}
                      aria-label={t('sidebar.inWorkRequestsTitle')}
                    >
                      {inWorkRequestsCount === null ? '…' : inWorkRequestsCount}
                    </span>
                  )}
                  {showPayoutBadges && (
                    <span className="ml-auto flex shrink-0 items-center gap-1">
                      <span
                        title={t('sidebar.payoutAvailableTitle')}
                        className={`min-w-[1.45rem] rounded-md px-1.5 py-0.5 text-center text-[11px] font-bold tabular-nums ${
                          (payoutAvailableCount ?? 0) > 0 ? 'bg-sky-400/90 text-sky-950' : 'bg-white/15 text-white/80'
                        }`}
                        aria-label={t('sidebar.payoutAvailableTitle')}
                      >
                        {payoutAvailableCount === null ? '…' : payoutAvailableCount}
                      </span>
                      <span
                        title={t('sidebar.payoutActiveTitle')}
                        className={`min-w-[1.45rem] rounded-md px-1.5 py-0.5 text-center text-[11px] font-bold tabular-nums ${
                          (payoutActiveCount ?? 0) > 0 ? 'bg-emerald-400/90 text-emerald-950' : 'bg-white/15 text-white/80'
                        }`}
                        aria-label={t('sidebar.payoutActiveTitle')}
                      >
                        {payoutActiveCount === null ? '…' : payoutActiveCount}
                      </span>
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="mb-4 px-2 py-3 border-b border-white/10">
            <div className="space-y-1.5">
              <div className="text-[11px] font-semibold text-white/85">{t('sidebar.intakeTitle')}</div>
              {vndTiersLoading ? (
                <div className="text-xs text-white/60">{t('common.loadingEllipsis')}</div>
              ) : vndTiersError ? (
                <div className="text-xs text-white/60">{vndTiersError}</div>
              ) : sortedVndTiers.length === 0 ? (
                <div className="text-xs text-white/60">{t('common.notAvailable')}</div>
              ) : (
                sortedVndTiers.map((tier, i) => (
                  <div
                    key={tier.id ?? `${tier.min_vnd}-${tier.max_vnd}-${i}`}
                    className="flex items-center justify-between text-xs bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10"
                  >
                    <span className="text-white/80">
                      {t('sidebar.rateFromTo', { from: formatRangeInt(tier.min_vnd), to: formatRangeInt(tier.max_vnd) })}
                    </span>
                    <span className="font-semibold text-white">{Number(tier.vnd_per_usdt).toFixed(2)}</span>
                  </div>
                ))
              )}

              {user?.can_process_payouts ? (
                <>
                  <div className="pt-2 text-[11px] font-semibold text-white/85">{t('sidebar.payoutTitle')}</div>
                  {vndPayoutTiersLoading ? (
                    <div className="text-xs text-white/60">{t('common.loadingEllipsis')}</div>
                  ) : vndPayoutTiersError ? (
                    <div className="text-xs text-white/60">{vndPayoutTiersError}</div>
                  ) : sortedVndPayoutTiers.length === 0 ? (
                    <div className="text-xs text-white/60">{t('common.notAvailable')}</div>
                  ) : (
                    sortedVndPayoutTiers.map((tier, i) => (
                      <div
                        key={tier.id ?? `payout-${tier.min_vnd}-${tier.max_vnd}-${i}`}
                        className="flex items-center justify-between text-xs bg-white/5 rounded-lg px-2.5 py-1.5 border border-white/10"
                      >
                        <span className="text-white/80">
                          {t('sidebar.rateFromTo', { from: formatRangeInt(tier.min_vnd), to: formatRangeInt(tier.max_vnd) })}
                        </span>
                        <span className="font-semibold text-white">{Number(tier.rate_percent ?? 0).toFixed(2)}%</span>
                      </div>
                    ))
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
        </div>
        <div className="border-t border-red-400/20 dark:border-white/10 pb-2 shrink-0">
          {showPagination &&
            totalPages > 1 &&
            (location.pathname === '/requests' ||
              location.pathname === '/payouts' ||
              location.pathname === '/transactions') && (
            <div className="p-4 pb-3 border-b border-white/10">
              <div className="text-[10px] uppercase tracking-wide text-white/50 mb-2">
                {location.pathname === '/requests'
                  ? t('sidebar.paginationRequests')
                  : location.pathname === '/payouts'
                    ? t('nav.payouts')
                  : t('sidebar.paginationHistory')}
              </div>
              <div className="flex items-center justify-between space-x-2">
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs border border-white/30 rounded-md hover:bg-white/10 disabled:opacity-50 text-white/70"
                >
                  ←
                </button>
                <span className="text-xs text-white/70 px-2 tabular-nums">{currentPage} / {totalPages}</span>
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs border border-white/30 rounded-md hover:bg-white/10 disabled:opacity-50 text-white/70"
                >
                  →
                </button>
              </div>
            </div>
          )}
          <div className="p-4 pt-3">
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-3 mb-4 rounded-lg hover:bg-white/5 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <FiUser className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="font-medium truncate">{user?.name || t('sidebar.counterparty')}</p>
            </div>
          </button>
            <button
              type="button"
              onClick={() => {
                if (isResponsible) navigate('/transactions');
              }}
              className={`w-full mb-3 px-3 py-2 bg-white/10 rounded-lg transition-colors text-left ${isResponsible ? 'hover:bg-white/15' : 'cursor-default'}`}
            >
              <p className="text-xs text-white/70">{t('sidebar.teamBalance')}</p>
              <p
                className="font-semibold text-yellow-300 cursor-copy inline-block hover:text-yellow-200 transition-colors"
                title={t('sidebar.copyBalanceTitle')}
                onClick={handleCopyBalance}
              >
                {formatCurrency(teamBalance)}
              </p>
            </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-white/80 hover:bg-white/10 rounded-lg transition-colors"
          >
            <FiLogOut className="w-4 h-4" />
            <span>{t('sidebar.logout')}</span>
          </button>
          </div>
        </div>
        </div>
      </aside>
    </>
  );
}
